import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    ref,
    set,
    onValue,
    onDisconnect,
    serverTimestamp,
    query,
    orderByKey,
    limitToLast,
    Unsubscribe,
} from 'firebase/database';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { rtdb } from '../providers/firebase/firebase';
import type { TradePolymarket } from '../types/types';
import { usePresenceInstanceId } from './usePresenceInstanceId';

const MAX_TRADES = 200;

/** Sanitize a market ID for use in RTDB paths (mirrors bogg's sanitize_rtdb_path_segment). */
function sanitizeMarketId(id: string): string {
    return id
        .replace(/\+/g, '-plus-')
        .replace(/@/g, '-at-')
        .replace(/\./g, '-dot-')
        .replace(/[/#$[\]]/g, '_');
}

/**
 * Subscribe to live Polymarket trades streamed to Firebase RTDB by bogg.
 *
 * - Writes presence at `presence/market_trades/{uid}/{instanceId}` with the watched market list
 * - Subscribes to `market_trades/{sanitized_market_id}` for each market
 * - Backend re-reads the presence subtree on each poll, so changing watched markets
 *   simply updates this instance's presence node; no disconnect/reconnect needed
 * - Uses `orderByKey().limitToLast(N)` to bound reads
 *
 * @param marketIds  Array of condition_id strings to watch (empty = no subscription)
 * @param makerOnly  If true, backend only streams maker fills (avoids volume double-counting)
 */
export function useMarketTradesStream(
    marketIds: string[],
    makerOnly = true,
): { trades: Map<string, TradePolymarket[]>; connected: boolean } {
    const [trades, setTrades] = useState<Map<string, TradePolymarket[]>>(new Map());
    const [connected, setConnected] = useState(false);
    const unsubAuthRef = useRef<(() => void) | null>(null);
    const unsubDataRef = useRef<Unsubscribe[]>([]);
    const presenceRefPath = useRef<string | null>(null);
    const instanceId = usePresenceInstanceId();

    // Stable key for the market list so useEffect only re-runs when the list actually changes
    const marketsKey = useMemo(() => [...marketIds].sort().join(','), [marketIds]);

    const cleanup = useCallback(() => {
        // Unsubscribe all data listeners
        for (const unsub of unsubDataRef.current) unsub();
        unsubDataRef.current = [];

        // Remove presence
        if (presenceRefPath.current) {
            set(ref(rtdb, presenceRefPath.current), null);
            presenceRefPath.current = null;
        }

        unsubAuthRef.current?.();
        unsubAuthRef.current = null;

        setConnected(false);
        setTrades(new Map());
    }, []);

    useEffect(() => {
        if (marketIds.length === 0) return;

        let cancelled = false;
        const auth = getAuth();

        function subscribe() {
            if (cancelled) return;

            const uid = auth.currentUser?.uid;
            if (!uid) return;

            // Write presence with watched markets, keyed per hook instance so
            // multiple tabs of the same user do not overwrite each other.
            const pPath = `presence/market_trades/${uid}/${instanceId}`;
            presenceRefPath.current = pPath;
            const presenceRef = ref(rtdb, pPath);
            set(presenceRef, {
                online: true,
                connectedAt: serverTimestamp(),
                markets: marketIds,
                maker_only: makerOnly,
            });
            onDisconnect(presenceRef).remove();

            // Subscribe to each market
            for (const marketId of marketIds) {
                const safeMid = sanitizeMarketId(marketId);
                const dataRef = query(
                    ref(rtdb, `market_trades/${safeMid}`),
                    orderByKey(),
                    limitToLast(MAX_TRADES),
                );

                let firstSnapshot = true;
                let prevJson: string | null = null;

                const unsub = onValue(
                    dataRef,
                    (snapshot) => {
                        if (firstSnapshot) {
                            setConnected(true);
                            firstSnapshot = false;
                        }
                        const val = snapshot.val();
                        if (val) {
                            const json = JSON.stringify(val);
                            if (json === prevJson) return;
                            prevJson = json;

                            // val is Record<tradeKey, TradePolymarket>
                            const tradeList: TradePolymarket[] = Object.values(val);
                            // Keys are {timestamp_millis}_{tx_id} — already ordered
                            setTrades(prev => {
                                const next = new Map(prev);
                                next.set(marketId, tradeList);
                                return next;
                            });
                        } else if (prevJson !== null) {
                            prevJson = null;
                            setTrades(prev => {
                                const next = new Map(prev);
                                next.delete(marketId);
                                return next;
                            });
                        }
                    },
                    (error) => {
                        console.error(`RTDB market_trades error for ${marketId}:`, error);
                        setConnected(false);
                    },
                );
                unsubDataRef.current.push(unsub);
            }
        }

        if (auth.currentUser) {
            subscribe();
        } else {
            const unsubAuth = onAuthStateChanged(auth, (user) => {
                if (user && !cancelled) {
                    unsubAuth();
                    unsubAuthRef.current = null;
                    subscribe();
                }
            });
            unsubAuthRef.current = unsubAuth;
        }

        return () => {
            cancelled = true;
            cleanup();
        };
    }, [marketsKey, makerOnly, cleanup]); // eslint-disable-line react-hooks/exhaustive-deps

    return { trades, connected };
}
